import {
  SitecoreContext,
  ComponentPropsContext,
} from "@sitecore-jss/sitecore-jss-nextjs";
import { handleEditorFastRefresh } from "@sitecore-jss/sitecore-jss-nextjs/utils";
import { GetServerSideProps } from "next";
import Head from "next/head";
import { useEffect } from "react";
import { componentBuilder } from "@repo/sitecore-lib/component-builder";
import { SitecorePageProps } from "@repo/sitecore-lib/page-props";
import { sitecorePagePropsFactory } from "@repo/sitecore-lib/page-props-factory";
import { ErrorMessage } from "@/components/error-message/error-message";
import BaseLayout from "@/components/sitecore-components/layouts/base-layout";
import HelpLayout from "@/components/sitecore-components/layouts/help-layout";
import MainLayout from "@/components/sitecore-components/layouts/main-layout";
import { getLayout } from "@/components/sitecore-components/layouts/utils";
import { SpinnerIcon } from "@/theme/icons/spinner";
import { getErrorMessage } from "@/utils/error-messages";
import { generateHreflangLinks } from "@/utils/hreflang";
import { GlobalLanding } from "@/views/global-landing";

type SitecoreProps = SitecorePageProps & {
  isLanding?: boolean;
  activeFloor: "topshop" | "topman";
  showOverlayAnimation?: boolean;
  isLandingRoute?: boolean;
};

const SitecorePage = ({
  notFound,
  componentProps,
  layoutData,
  headLinks,
  isLanding,
}: SitecoreProps): JSX.Element => {
  const isEditing = layoutData?.sitecore?.context?.pageEditing ?? false;
  useEffect(() => {
    handleEditorFastRefresh();
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.body.setAttribute("data-editing", isEditing ? "true" : "false");
    }
  }, [isEditing]);

  if (isLanding && !isEditing) {
    const hreflangLinks = generateHreflangLinks();

    return (
      <>
        <Head>
          {hreflangLinks.map(({ hrefLang, href }) => (
            <link
              key={hrefLang}
              rel="alternate"
              hrefLang={hrefLang}
              href={href}
            />
          ))}
        </Head>
        <BaseLayout layoutData={layoutData} headLinks={headLinks}>
          <GlobalLanding />
        </BaseLayout>
      </>
    );
  }

  if (isEditing && !layoutData?.sitecore?.route) {
    console.warn(
      "Editing mode: layoutData or route is missing, showing loading state",
    );
    return <SpinnerIcon label="Loading Editing Content" />;
  }

  if (notFound || !layoutData?.sitecore?.route) {
    const { title, message } = getErrorMessage("404");
    return <ErrorMessage errorCode="404" title={title} message={message} />;
  }

  const templateName = layoutData.sitecore.route.templateName;
  const showHelpLayout = getLayout(templateName as string);

  let Layout = MainLayout;

  if (showHelpLayout) {
    Layout = HelpLayout;
  }

  return (
    <ComponentPropsContext value={componentProps}>
      <SitecoreContext
        componentFactory={componentBuilder.getComponentFactory({ isEditing })}
        layoutData={layoutData}
      >
        <Layout layoutData={layoutData} headLinks={headLinks} />
      </SitecoreContext>
    </ComponentPropsContext>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const path = context.params?.path;
  let isLanding = false;
  if (path?.length === 1) {
    isLanding = true;
  }

  try {
    const props = await sitecorePagePropsFactory.create(context);

    if (props.layoutData?.sitecore?.context.pageEditing) {
      console.log("Editing request - props notFound:", props.notFound);
      console.log(
        "Editing request - has route:",
        !!props.layoutData?.sitecore?.route,
      );
      // In editing mode, always return props even if some data is missing
      // This prevents the hard 404 and allows the editor to show something
      return {
        props: {
          ...props,
          notFound: false,
        },
        notFound: false,
      };
    }
    return {
      props: {
        ...props,
        isLanding,
      },
      notFound: props.notFound,
    };
  } catch (err) {
    console.log(await err);
    return {
      notFound: true,
    };
  }
};

export default SitecorePage;
